import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Loader2, CalendarIcon } from "lucide-react";

function fmtPickerDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function DateField({ testId, label, value, onChange }) {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testId} className="text-xs uppercase text-white/60 flex items-center gap-2 border border-white/20 px-3 py-2 hover:border-white/40">
          <CalendarIcon className="w-3.5 h-3.5" />
          {label}: {value || "Any"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-surface border-white/10">
        <Calendar mode="single" selected={selected} onSelect={(d) => onChange(fmtPickerDate(d) || "")} initialFocus />
        {value && (
          <button onClick={() => onChange("")} className="w-full text-xs uppercase text-white/50 py-2 border-t border-white/10 hover:text-white">
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

const CATEGORY_LABELS = {
  newsletter_subscriber: "Newsletter Subscribers",
  voluntary_lead: "Voluntary Leads",
  checkout_initiator: "Checkout Initiators",
  abandoned_checkout: "Abandoned Checkouts",
  confirmed_customer: "Confirmed Customers",
  activated_purchaser: "Activated Purchasers",
  gold_prospect: "Gold Prospects",
  gold_pro_prospect: "Gold Pro Prospects",
  book_prospect: "Book Prospects",
  muscle_school_prospect: "Muscle School Prospects",
};

export default function AdminGrowth() {
  const api = useApi();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [product, setProduct] = useState("all");
  const [consent, setConsent] = useState("all");
  const [category, setCategory] = useState("");
  const [exporting, setExporting] = useState(false);

  const filters = {
    date_from: dateFrom, date_to: dateTo,
    product: product === "all" ? "" : product,
    consent: consent === "all" ? "" : consent,
    category,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["admin-leads-dashboard", filters],
    queryFn: async () => (await api.get("/admin/leads/dashboard", { params: filters })).data,
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/leads/export.csv", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "endure_beyond_leads_export.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch {
      toast.error("Could not export leads.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-growth-page">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <h1 className="font-display text-display-xs">Leads Dashboard</h1>
        <button data-testid="admin-leads-export-btn" onClick={exportCsv} disabled={exporting}
          className="text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50 disabled:opacity-40">
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>
      <p className="text-xs text-foreground/60 mb-6">Live aggregation across newsletter signups, Stripe checkout activity, registrations, and entitlements — not a separate stored table, so it can never drift from the real records.</p>

      <div className="flex flex-wrap gap-3 mb-6 border border-white/10 p-3">
        <DateField testId="admin-leads-date-from" label="From" value={dateFrom} onChange={setDateFrom} />
        <DateField testId="admin-leads-date-to" label="To" value={dateTo} onChange={setDateTo} />
        <Select value={product} onValueChange={setProduct}>
          <SelectTrigger data-testid="admin-leads-product-filter" className="w-44 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any product interest</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="gold_pro">Gold Pro</SelectItem>
            <SelectItem value="book">Books</SelectItem>
            <SelectItem value="muscle_school">Muscle School</SelectItem>
          </SelectContent>
        </Select>
        <Select value={consent} onValueChange={setConsent}>
          <SelectTrigger data-testid="admin-leads-consent-filter" className="w-48 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any marketing consent</SelectItem>
            <SelectItem value="yes">Consented</SelectItem>
            <SelectItem value="no">Not consented</SelectItem>
          </SelectContent>
        </Select>
        {category && (
          <button data-testid="admin-leads-clear-category" onClick={() => setCategory("")} className="text-xs uppercase text-white/50 underline">Clear category filter</button>
        )}
      </div>

      {isLoading && <p className="text-white/60 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}

      {data && (
        <>
          <div className="flex flex-wrap gap-3 mb-6 text-xs text-white/60">
            <Badge variant="outline" data-testid="admin-leads-total">{data.total} records</Badge>
            <Badge variant="outline" data-testid="admin-leads-duplicates">{data.duplicate_emails} duplicate emails</Badge>
            <Badge variant="outline" data-testid="admin-leads-genuine">{data.genuine_count} genuine</Badge>
            <Badge variant="outline" data-testid="admin-leads-dev">{data.development_count} development/test</Badge>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              const c = data.counts[key] || { total: 0, genuine: 0, development: 0 };
              return (
                <button key={key} data-testid={`admin-leads-category-${key}`} onClick={() => setCategory(category === key ? "" : key)}
                  className={`border p-3 text-left transition-colors ${category === key ? "border-brandAccent text-brandAccent" : "border-white/10 hover:border-white/30"}`}>
                  <p className="text-2xl font-display">{c.total}</p>
                  <p className="text-[11px] uppercase tracking-wide text-white/50">{label}</p>
                  <p className="text-[10px] text-white/30 mt-1">{c.genuine} genuine · {c.development} test/dev</p>
                </button>
              );
            })}
          </div>

          <div className="divide-y divide-white/10 border-t border-b border-white/10" data-testid="admin-leads-table">
            {data.records.slice(0, 100).map((r, i) => (
              <div key={`${r.email}-${r.category}-${i}`} className="flex items-center justify-between gap-3 py-2.5 text-sm flex-wrap" data-testid={`admin-leads-row-${i}`}>
                <span className="truncate">{r.email}</span>
                <span className="flex items-center gap-2 flex-wrap text-xs">
                  <Badge variant="outline">{CATEGORY_LABELS[r.category] || r.category}</Badge>
                  {r.product_interest && <Badge variant="outline">{r.product_interest}</Badge>}
                  <Badge className={r.marketing_consent ? "bg-green-500/10 text-green-300 border-green-500/30" : "bg-white/5 text-white/50 border-white/20"}>
                    {r.marketing_consent ? "Consented" : "No consent"}
                  </Badge>
                  <span className="text-white/40">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</span>
                </span>
              </div>
            ))}
            {data.records.length === 0 && <p data-testid="admin-leads-empty" className="text-white/50 text-sm py-6">No records match these filters.</p>}
          </div>
          {data.records.length > 100 && <p className="text-xs text-white/40 mt-3">Showing the first 100 of {data.records.length} — use Export CSV for the full list.</p>}
        </>
      )}
    </div>
  );
}
